import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import {
  ActivityTypes,
  FetchClassificatorsService,
  GoalTypes,
  ObservationTypes,
  PeriodRepeats,
  StatusTypes,
  UnitValues,
} from "./fetch-classificators.service";
import {
  HealthPlanActivities,
  HealthPlanActivitiesReport,
  HealthPlanData,
  HealthPlanGoals,
} from "../models/health-plan.model";
import { ObservationData } from "../models/observation.model";
import { NgxMeasurementsService, round } from "./ngx-measurements.service";
import { NgxHealthPlanService } from "../features/health-plan/ngx-health-plan.service";
import { format, startOfDay, startOfISOWeek, startOfMonth, startOfWeek, startOfYear } from "date-fns";

@Injectable({
  providedIn: "root",
})
export class ChartService {
  obsTypes: ObservationTypes;
  activityTypes: ActivityTypes;
  healthPlanStatus: StatusTypes;
  repeatPeriod: PeriodRepeats;
  unitValues: UnitValues;
  goalTypes: GoalTypes;
  private hasClassificators: boolean;
  private movementCodes = [];
  private _forceExpandChart = new BehaviorSubject(null);
  forceExpandChart = this._forceExpandChart.asObservable();

  constructor(private classificatorService: FetchClassificatorsService,
              private ngxHealthPlanService: NgxHealthPlanService,
              private translate: TranslateService,
              private measurementsService: NgxMeasurementsService) {

    this.classificatorService.hasClassificators.subscribe((data) => {
      if (data == true) {
        this.hasClassificators = data;
        this.obsTypes = this.classificatorService.observationTypes.getValue();
        this.unitValues = this.classificatorService.unitValues.getValue();
        this.activityTypes = this.classificatorService.activityTypes.getValue();
        this.healthPlanStatus = this.classificatorService.statusTypes.getValue();
        this.repeatPeriod = this.classificatorService.repeatPeriods.getValue();
        this.goalTypes = this.classificatorService.goalTypes.getValue();

        this.movementCodes = [
          this.obsTypes.movement_LIGHTLY,
          this.obsTypes.movement_MODERATELY,
          this.obsTypes.movement_VERY,
        ];
      }
    });
  }

  expand(id: string) {
    this._forceExpandChart.next(id);
  }

  generateChartData(sd: ChartServiceData) {
    // console.debug(sd);
    if (!this.hasClassificators) return [];
    let returnSets = [];

    if (sd.goal && sd.isShowLines) {
      if (sd.isWeekView) {
        returnSets.push(...this.averageStackLines([...this.getDayObservations(sd.personId, sd.goal)]));
      } else if (sd.isDayView) {
        returnSets.push(...this.getDayObservations(sd.personId, sd.goal));
      }
    }

    if (sd.activity && sd.isShowBars && sd.isWeekView && !sd.getAllActivities) {
      returnSets.push(...this.getActivitySeries(sd.personId, sd.activity));
    }

    if (sd.getAllActivities && sd.isShowBars && sd.isWeekView) {
      returnSets.push(...this.getAllWeekActivities(sd.personId));
    }

    if (sd.observationChartType && sd.isDayView) {
      returnSets = this.getObservations(sd.observationChartType, sd.personId);
    } else if (sd.observationChartType && sd.isWeekView) {
      returnSets = this.averageStackLines(this.getObservations(sd.observationChartType, sd.personId));
    }

    returnSets = returnSets.filter((element) => {
      if (element === undefined) {
        return false;
      }
      if (!element.data) {
        return false;
      }
      return element.data.length !== 0;
    });

    if (sd.isStacked) {
      returnSets = this.stackBars(returnSets);
    }

    return returnSets;
  }

  stackBars(returnSets: Series[]) {
    let returnDataBarsStacked: Series[] = [];
    let allBarSets = returnSets.filter(set => set.type === "bar");
    let barSetNames = [...new Set(allBarSets.map(set => set.subtitle))];
    for (let name of barSetNames) {
      let groupedSets = allBarSets.filter(set => set.subtitle === name);
      let targetSet = groupedSets[groupedSets.length - 1];

      let stackedWeekData = [];

      for (let series of groupedSets) {
        for (let item of series.data) {
          stackedWeekData.push(item);
        }
      }
      stackedWeekData = reduceToWeekArrayStack(stackedWeekData, "isoWeek");
      if (targetSet) {
        targetSet.data = stackedWeekData;
        returnDataBarsStacked.push(targetSet);
      }
    }

    for (let set of returnSets) {
      if (["line", "scatter"].includes(set.type)) {
        returnDataBarsStacked.push(set);
      }
    }
    return returnDataBarsStacked;
  }

  private getChartClassificators(chartType: ObservationChartType): number[] {
    const classificatorCodeList = [];

    switch (chartType) {
      case ObservationChartType.BLOOD_SUGAR:
        classificatorCodeList.push(this.obsTypes.bloodSugar);
        break;
      case ObservationChartType.BLOOD_PRESSURE:
        classificatorCodeList.push(this.obsTypes.systolic, this.obsTypes.diastolic, this.obsTypes.pulse);
        break;
      case ObservationChartType.HEIGHT_WEIGHT:
        classificatorCodeList.push(this.obsTypes.height, this.obsTypes.weight, this.obsTypes.waist);
        break;
      case ObservationChartType.PEF:
        classificatorCodeList.push(this.obsTypes.PEF);
        break;
    }

    return classificatorCodeList;
  }

  private getObservations(chartType: ObservationChartType, personId: number): Series[] {
    const series = [];
    const classificatorList = this.getChartClassificators(chartType);
    const classificatorObservationCollection = new Map<number, ObservationData[]>();

    classificatorList.forEach(classificator => classificatorObservationCollection.set(classificator, []));

    this.getPersonObs(personId).forEach(observation => {
      const currentObservationList = classificatorObservationCollection.get(observation.classificatorCodeId);

      if (currentObservationList) {
        currentObservationList.push(observation);
      }
    });

    let index = 0;
    for (const [classificator, observationGroup] of classificatorObservationCollection.entries()) {
      series.push(this.generateObsDaySeries(observationGroup, classificator, index++));
    }

    return series;
  }

  getActivitySeries(personId: number, activity: HealthPlanActivities) {
    let target = this.ngxHealthPlanService.activitiesHealthPlan.find(target => target.id === activity.id);

    if (!target) {
      return [];
    }

    const returnSeriesArray: Series[] = [];
    let index = 0;
    for (let classificatorCode of this.movementCodes) {
      if (target.observationList) {
        let filteredPersonObs = this.measurementsService.getObsAsSingles(target.observationList)
          .filter(obs => {
            return obs.personId === personId &&
              obs.classificatorCodeId == classificatorCode;
          });

        returnSeriesArray.push(this.generateObsWeekSeries(filteredPersonObs, classificatorCode, index, null, activity));
        index++;
      }
    }

    if (target) {
      returnSeriesArray.push(this.generateDurationWeekSeries(target.reportsList, index, null, null, target));
    }

    return returnSeriesArray;
  }

  getDayObservations(personId: number, goal: HealthPlanGoals) {
    const personObs = this.getPersonObs(personId);
    const classificatorCodes = this.measurementsService.getClassificatorCodesByGoalType(goal.typeId);
    const codesSorted = this.sortOrder(classificatorCodes);

    if (!codesSorted) {
      return;
    }

    const returnSeriesArray: Series[] = [];
    let i = 0;
    for (let classificatorCode of codesSorted) {
      let filteredPersonObs = personObs.filter(obs => obs.classificatorCodeId == classificatorCode);
      returnSeriesArray.push(this.generateObsDaySeries(filteredPersonObs, classificatorCode, i, goal, null));
      i++;
    }
    return returnSeriesArray;
  }

  getAllWeekActivities(personId: number) {
    let modifiedArray = [];
    let allActivities = this.ngxHealthPlanService.activitiesHealthPlan.filter(activity => activity.personId === personId);
    for (let activity of allActivities) {
      let activityStack = this.getActivitySeries(personId, activity);
      modifiedArray.push(...activityStack);
    }
    return modifiedArray;
  }

  getWeekActivity(personId: number, activity: HealthPlanActivities) {
    return this.getActivitySeries(personId, activity);
  }

  averageStackLines(returnSets: Series[]) {

    let returnDataLinesAveraged: any[] = [];
    let lineSets = returnSets.filter(set => ["line", "scatter"].includes(set.type));
    for (let series of lineSets) {
      let stackedWeekData = [];
      for (let item of series.data) {
        item[0] = format(startOfISOWeek(new Date(item[0])), "yyyy-MM-dd");
        stackedWeekData.push(item);
      }
      stackedWeekData = reduceToWeekArrayAverage(stackedWeekData, "isoWeek");
      series.data = stackedWeekData;
      returnDataLinesAveraged.push(series);
    }

    for (let set of returnSets) {
      if (["bar"].includes(set.type)) {
        returnDataLinesAveraged.push(set);
      }
    }
    return returnDataLinesAveraged;
  }

  sortOrder(arr: any[]) {
    if (!arr) return;
    return arr.sort((a, b) => a - b);
  }

  private generateObsDaySeries(filteredPersonObs: ObservationData[], code: number, index: number, goal?: HealthPlanGoals, activity?: HealthPlanActivities) {
    let series: Series =
      {
        name: this.getNameFromObsType(code),
        subtitle: activity ? activity.description : null,
        type: this.getDataType(code),
        yAxisIndex: index,
        classificatorCode: code,
        smooth: true,
        tooltip: {},
        goalTypeId: goal ? goal.typeId : null,
        activityTypeId: activity ? activity.typeId : null,
        activityTarget: null,
        markArea: null,
        markLine: { data: [] },
        data: this.getData(filteredPersonObs, code),
      };
    return series;
  }

  private getData(filteredPersonObs: ObservationData[], classificatorCode: number): any[] {
    let returnArray: any[] = [];
    for (let obs of filteredPersonObs) {
      if (obs.hasOwnProperty("value") && (obs.hasOwnProperty("insertTime") || obs.hasOwnProperty("effectiveTime"))) {
        this.transformValue(obs);
        returnArray.push(
          [
            obs.effectiveTime ? getSecondString(obs.effectiveTime) : getSecondString(obs.insertTime),
            round(parseFloat(obs.value), 1),
          ]);
      }
    }
    let sortedItems = sortItems(returnArray);
    sortedItems.forEach(item => item.push(this.getLabel(classificatorCode)));
    return sortedItems;
  }

  private generateDurationWeekSeries(reports: HealthPlanActivitiesReport[], code: number, index: number, goal?: HealthPlanGoals, activity?: HealthPlanActivities) {
    if (!reports) {
      return;
    }

    if (!activity) {
      return;
    }

    let classificatorCode = code;

    if (activity) {
      classificatorCode = activity.typeId;
    }

    let series: Series =
      {
        name: this.getNameFromObsType(classificatorCode),
        subtitle: activity ? activity.description : null,
        type: this.getDataType(code),
        yAxisIndex: index,
        classificatorCode: code,
        smooth: true,
        tooltip: {},
        goalTypeId: goal ? goal.typeId : null,
        activityTypeId: activity ? activity.typeId : null,
        markArea: null,
        markLine: { data: [] },
        activityTarget: this.calculateActivityTarget(activity),
        data: [],
      };

    if (activity.repeatPeriodId === this.repeatPeriod.WEEK && activity.repeatFrequency && activity.repeatDuration) {
      series.data = this.getDurationWeekDataMinutes(reports, activity.typeId);
    } else if (activity.repeatPeriodId === this.repeatPeriod.WEEK && activity.repeatFrequency && !activity.repeatDuration) {
      const label = this.translate.instant("healthPlan.type_times");
      series.data = this.getDurationWeekDataTimes(reports, activity.typeId, label);
    } else if (activity.repeatPeriodId === this.repeatPeriod.CONTINUOUS) {
      const label = this.translate.instant("healthPlan.type_times");
      series.data = this.getDurationWeekDataTimes(reports, activity.typeId, label);
    }

    return series;
  }

  getDurationWeekDataTimes(reports: HealthPlanActivitiesReport[], typeId: number, label?: string): any[] {
    let dataArray: any[] = [];
    for (let report of reports) {
      if (this.isValidTimeReport(report)) {
        dataArray.push(
          [
            report.effectiveTime ? getWeekStartString(report.effectiveTime) : getWeekStartString(report.insertTime),
            report.repeatValue,
            label ? label : this.getLabel(typeId),
          ]);
      }
    }
    let reducedArray = reduceToWeekArrayStack(dataArray, "isoWeek");
    return sortItems(reducedArray);
  }

  getDurationWeekDataMinutes(reports: HealthPlanActivitiesReport[], typeId: number): any[] {
    let dataArray: any[] = [];
    for (let report of reports) {
      if (this.isValidTimeReport(report)) {
        dataArray.push(
          [
            report.effectiveTime ? getWeekStartString(report.effectiveTime) : getWeekStartString(report.insertTime),
            this.parseDurationToMinutes(report.durationValue),
            this.getLabel(typeId),
          ]);
      }
    }
    let reducedArray = reduceToWeekArrayStack(dataArray, "isoWeek");
    return sortItems(reducedArray);
  }

  parseDurationToMinutes(timeString: string): number {
    if (!timeString) return 0;
    const [hours, minutes, seconds] = timeString.split(":").map(Number);
    return (hours || 0) * 60 + (minutes || 0) + ((seconds || 0) / 60);
  }

  isValidTimeReport(report: HealthPlanActivitiesReport) {
    return report.hasOwnProperty("durationValue") || report.hasOwnProperty("repeatValue") &&
      (report.hasOwnProperty("insertTime") || report.hasOwnProperty("effectiveTime"));
  }

  private generateObsWeekSeries(filteredPersonObs: ObservationData[], classificatorCode: number, index: number, goal?: HealthPlanGoals, activity?: HealthPlanActivities) {
    let series: Series =
      {
        name: this.getNameFromObsType(classificatorCode),
        subtitle: activity ? activity.description : null,
        type: this.getDataType(classificatorCode),
        yAxisIndex: index,
        classificatorCode: classificatorCode,
        smooth: true,
        tooltip: {},
        goalTypeId: goal ? goal.typeId : null,
        activityTypeId: activity ? activity.typeId : null,
        activityTarget: null,
        markArea: null,
        markLine: { data: [] },
        data: this.getObservationWeekData(filteredPersonObs, classificatorCode),
      };
    return series;
  }

  getObservationWeekData(filteredPersonObs: ObservationData[], classificatorCode: number) {
    let dataArray: any[] = [];
    for (let obs of filteredPersonObs) {
      if (obs.hasOwnProperty("value") && (obs.hasOwnProperty("insertTime") || obs.hasOwnProperty("effectiveTime"))) {
        dataArray.push(
          [
            obs.effectiveTime ? getWeekStartString(obs.effectiveTime) : getWeekStartString(obs.insertTime),
            round(parseFloat(obs.value), 1),
            this.getLabel(classificatorCode),
          ]);
      }
    }
    let reducedArray = reduceToWeekArrayStack(dataArray, "isoWeek");
    return sortItems(reducedArray);
  }

  transformValue(obs: ObservationData) {
    if (obs.valueUnitId == this.unitValues.mg_dl) {
      obs.value = String(mgdlToMmol(obs.value));
    }
    if (obs.valueUnitId == this.unitValues.m) {
      obs.value = String(mToCm(obs.value));
    }
  }

  getDataType(code: number): string {
    if ([
      this.obsTypes.systolic,
      this.obsTypes.diastolic,
      this.obsTypes.bloodSugar,
      this.obsTypes.height,
      this.obsTypes.waist,
      this.obsTypes.weight,
      this.obsTypes.PEF,
    ].includes(code)) {
      return "line";
    } else if ([this.obsTypes.pulse].includes(code)) {
      return "scatter";
    } else {
      return "bar";
    }
  }

  private getPersonObs(personId: number) {
    return this.measurementsService.getObsAsSingles().filter((obs) => {
      return obs?.personId == personId;
    });
  }

  getNameFromObsType(obsClassificatorCodeId: number): string {
    if (obsClassificatorCodeId == this.obsTypes.PEF) {
      return this.translate.instant("healthPlan.asthmaPef");
    } else if (obsClassificatorCodeId == this.obsTypes.bloodSugar) {
      return this.translate.instant("healthPlan.obs_type_bloodSugar");
    } else if (obsClassificatorCodeId == this.obsTypes.weight) {
      return this.translate.instant("healthPlan.weight");
    } else if (obsClassificatorCodeId == this.obsTypes.waist) {
      return this.translate.instant("healthPlan.waist");
    } else if (obsClassificatorCodeId == this.obsTypes.height) {
      return this.translate.instant("healthPlan.height");
    } else if (obsClassificatorCodeId == this.obsTypes.BMI) {
      return this.translate.instant("healthPlan.obs_type_BMI");
    } else if (obsClassificatorCodeId == this.obsTypes.diastolic) {
      return this.translate.instant("healthPlan.diastolic");
    } else if (obsClassificatorCodeId == this.obsTypes.systolic) {
      return this.translate.instant("healthPlan.systolic");
    } else if (obsClassificatorCodeId == this.obsTypes.pulse) {
      return this.translate.instant("healthPlan.obs_type_pulse");
    } else if (obsClassificatorCodeId == this.obsTypes.movement_LIGHTLY) {
      return this.translate.instant("healthPlan.type_movement_light");
    } else if (obsClassificatorCodeId == this.obsTypes.movement_MODERATELY) {
      return this.translate.instant("healthPlan.type_movement_moderate");
    } else if (obsClassificatorCodeId == this.obsTypes.movement_VERY) {
      return this.translate.instant("healthPlan.type_movement_very");
    } else if (obsClassificatorCodeId == this.obsTypes.movementActivity) {
      return this.translate.instant("healthPlan.type_movement");
    } else if (obsClassificatorCodeId == this.activityTypes.MOOD) {
      return this.translate.instant("healthPlan.mood");
    } else if (obsClassificatorCodeId == this.activityTypes.PHYSICAL_HIGH) {
      return this.translate.instant("healthPlan.highIntensityPhysicalActivity");
    } else if (obsClassificatorCodeId == this.activityTypes.PHYSICAL_LOW) {
      return this.translate.instant("healthPlan.lowIntensityPhysicalActivity");
    } else if (obsClassificatorCodeId == this.activityTypes.HABIT) {
      return this.translate.instant("healthPlan.badHabit");
    } else if (obsClassificatorCodeId == this.activityTypes.SLEEP) {
      return this.translate.instant("healthPlan.sleep");
    } else if (obsClassificatorCodeId == this.activityTypes.NUTRITION) {
      return this.translate.instant("healthPlan.nutrition");
    } else if (obsClassificatorCodeId == this.activityTypes.MEDICATION) {
      return this.translate.instant("healthPlan.medications");
    } else if (obsClassificatorCodeId == this.activityTypes.MEASUREMENT) {
      return this.translate.instant("healthPlan.measurement");
    }
  }

  getLabel(classificatorCode: number) {
    if ([this.obsTypes.diastolic, this.obsTypes.systolic].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_mmHg");
    } else if ([this.obsTypes.pulse].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_bpm_min");
    } else if ([this.obsTypes.weight].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_kg");
    } else if ([this.obsTypes.height, this.obsTypes.waist].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_cm");
    } else if ([this.obsTypes.bloodSugar].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_mmol_l");
    } else if ([this.obsTypes.PEF].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_l_min");
    } else if ([
      this.obsTypes.movement_VERY,
      this.obsTypes.movement_MODERATELY,
      this.obsTypes.movement_LIGHTLY,
      this.obsTypes.movementActivity,
      this.activityTypes.PHYSICAL_HIGH,
      this.activityTypes.PHYSICAL_LOW,
      this.activityTypes.SLEEP,
    ].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_min");
    } else if ([
      this.activityTypes.MOOD,
      this.activityTypes.MEASUREMENT,
      this.activityTypes.NUTRITION,
      this.activityTypes.HABIT,
      this.activityTypes.MEDICATION,
    ].includes(classificatorCode)) {
      return this.translate.instant("healthPlan.type_times");
    }
    return;
  }

  calculateActivityTarget(activity: HealthPlanActivities) {
    if (
      activity.repeatPeriodId === this.repeatPeriod.WEEK &&
      activity.repeatFrequency &&
      activity.repeatDuration
    ) {
      return this.parseDurationToMinutes(activity.repeatDuration) * activity.repeatFrequency;
    } else if (activity.repeatPeriodId === this.repeatPeriod.WEEK && activity.repeatFrequency) {
      return activity.repeatFrequency;
    } else if (activity.repeatPeriodId === this.repeatPeriod.DAY && activity.repeatFrequency) {
      return activity.repeatFrequency * 7;
    } else if (activity.repeatPeriodId === this.repeatPeriod.CONTINUOUS) {
      return 1;
    }
  }

}

export interface Series {
  name: string;
  type: string;
  subtitle?: string;
  smooth: boolean;
  yAxisIndex: number;
  goalTypeId?: number;
  activityTypeId?: number;
  classificatorCode: number;
  tooltip: any;
  markArea: any;
  data: any[];
  activityTarget: number;
  markLine: Data;
}

export interface Data {
  data: any;
}


export function getWeekStartString(date: any): string {
  return format(startOfISOWeek(new Date(date)), "yyyy-MM-dd");
}

export function getDayString(date: any): string {
  return format(new Date(date), "yyyy-MM-dd");
}

export function getSecondString(date: any): string {
  const dt = new Date(date);
  dt.setMilliseconds(0);
  return format(dt, "yyyy-MM-dd'T'HH:mm:ss");
}


/**
 * Sorts arrays of arrays on index 0
 * @param returnArray [[],[]...]
 */
export function sortItems(returnArray: any[]) {
  return returnArray.sort((a, b) => {
    return new Date(a[0]).valueOf() - new Date(b[0]).valueOf();
  });
}

/**
 * Reduces arrays in array to timeframe
 * @param arrays [['2022-01-17T00:00:00.000+02:00', 240]...]
 * @param range string (isoWeek, year etc)
 */
export function reduceToWeekArrayStack(arrays: any[], range: string) {
  const startOfFn = getStartOfFunction(range);
  const reduced = arrays.reduce((a, b) => {
    const key = format(startOfFn(new Date(b[0])), "yyyy-MM-dd");
    if (a[key]) round(a[key][1] += b[1], 1);
    else a[key] = b;
    return a;
  }, {});
  return Object.values(reduced);
}

export function reduceToWeekArrayAverage(arrays: any[], range: string) {
  const startOfFn = getStartOfFunction(range);
  const reduced = arrays.reduce((a, b) => {
    const key = format(startOfFn(new Date(b[0])), "yyyy-MM-dd");
    if (a[key]) {
      a[key][1] = round((a[key][1] + b[1]) / 2, 1);
    } else a[key] = b;
    return a;
  }, {});
  return Object.values(reduced);
}

export function getStartOfFunction(range: string) {
  switch (range) {
    case "day":
      return startOfDay;
    case "week":
      return startOfWeek;
    case "isoWeek":
      return startOfISOWeek;
    case "month":
      return startOfMonth;
    case "year":
      return startOfYear;
    default:
      throw new Error(`Unsupported range: ${range}`);
  }
}

/**
 * mg/dL in mmol/L, conversion factor: 1 mg/dL = 0.0555 mmol/L
 */
export function mgdlToMmol(value: any) {
  return parseFloat(value) * 0.0555;
}

/**
 * m to cm factor: 100
 */
export function mToCm(value: any) {
  return parseFloat(value) * 100;
}

export const enum EgpChartType {
  MULTI_LINE = 1,
  COMBO = 2,
  LINE = 3,
  BAR = 4,
  MULTI_BAR = 5
}

export const enum EgpApplication {
  DOCTOR = "DOCTOR",
  PATIENT = "PATIENT"
}

export const enum EgpChartColor {
  MAIN = "#242e66",
  GREEN = "#1dd56a",
  YELLOW = "#ffbb04",
  DARK_GRAY = "#7b81a3",
  BLUE = "#0089ff",
  MIDDLE_MAIN = "rgba(36, 46, 102, 0.5)",
  LIGHT_MAIN = "rgba(36, 46, 102, 0.25)",
  LIGHTER_MAIN = "rgba(36, 46, 102, 0.15)",
  LIGHTEST_MAIN = "rgba(36, 46, 102, 0.05)",
  WHITE = "#ffffff"
}

export interface ChartServiceData {
  personId: number;
  goal: HealthPlanGoals;
  activity: HealthPlanActivities;
  isStacked: boolean;
  isShowBars: boolean;
  isShowLines: boolean;
  isSingleBars: boolean;
  getAllActivities: boolean;
  isDayView: boolean;
  isWeekView: boolean;
  fullScreen: any;
  currentZoom: any;
  seriesMinMax: any;
  healthPlan: HealthPlanData;
  zoomFactor: number;
  observationChartType: ObservationChartType,
  chartType: EgpChartType,
}

export const enum ObservationChartType {
  BLOOD_PRESSURE = "BLOOD_PRESSURE",
  BLOOD_SUGAR = "BLOOD_SUGAR",
  PEF = "PEF",
  HEIGHT_WEIGHT = "HEIGHT_WEIGHT",
}
